// ══════════════════════════════════════════════════════════════════════════════
// ErrorBoundary.tsx — React Error Boundary per catch errori di rendering
// ══════════════════════════════════════════════════════════════════════════════

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { reportError, mascheraTokenPubblici } from '../utils/errorReporter';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary che cattura errori di rendering React
 * Nota: deve essere class component (non supportato in hooks)
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Aggiorna lo state per mostrare UI di fallback
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Segnala l'errore al backend
    console.error('[ErrorBoundary] Errore catturato:', error, errorInfo);

    // I token dei link pubblici (/prenota/…) non devono finire nelle segnalazioni:
    // reportError li maschera comunque, qui lo si fa già a monte.
    reportError({
      tipo: 'frontend',
      message: mascheraTokenPubblici(`React Error: ${error.message}`),
      stack: error.stack ? mascheraTokenPubblici(error.stack) : undefined,
      url: mascheraTokenPubblici(window.location.href),
      userAgent: navigator.userAgent
    });
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Sulle pagine pubbliche (/prenota…) l'acquirente non deve vedere dettagli tecnici:
      // messaggio e stack possono contenere l'URL con il token del suo link.
      const { pathname } = window.location;
      const paginaPubblica = pathname === '/prenota' || pathname.startsWith('/prenota/');
      return (
        <div className="bop-error-boundary">
          <div className="bop-error-boundary-card">
            <div className="bop-error-boundary-icon">⚠️</div>
            <h1 className="bop-error-boundary-title">Si è verificato un errore</h1>
            <p className="bop-error-boundary-message">
              Qualcosa è andato storto. L'errore è stato segnalato automaticamente.
            </p>
            {this.state.error && !paginaPubblica && (
              <details className="bop-error-boundary-details">
                <summary className="bop-error-boundary-summary">Dettagli tecnici</summary>
                <pre className="bop-error-boundary-pre">
                  {mascheraTokenPubblici(this.state.error.message)}
                  {'\n\n'}
                  {mascheraTokenPubblici(this.state.error.stack ?? '')}
                </pre>
              </details>
            )}
            <button onClick={this.handleReload} className="bop-btn-primary">
              Ricarica la pagina
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
